import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../state/store";
import { SubmitHandler, useForm } from "react-hook-form";
import { Agency, BillingPeriod } from "../../../models/Agency";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import Button from "../../Button";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ReactiveFormSelect } from "../../form/ReactiveFormSelect";
import { ReactiveFormInput } from "../../form/ReactiveFormInput";
import { Toast } from "../../Toast";
import { createAgencyBillingPeriod, endAgencyBillingPeriod } from "../../../state/agencies/action";
import { LoadingMask } from "../../LoadingMask";
import { ToastContainer } from "react-toastify";

interface Props {
    currentAgency: Agency
}

export const AgencyBillingModule = (props: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const isLoading = useSelector((state: any) => state.agencies.isLoading);
    const [showError, setShowError] = useState(false);
    const error = useSelector((state: any) => state.agencies.error);
    const billingPeriods = useSelector((state: any) => state.agencies.billingPeriods.slice(0, 2));

    const billingPeriodForm = useForm<BillingPeriod>();

    const currentYear = new Date().getFullYear();

    const lastBillingPeriod = () => billingPeriods.filter((el: any) => !el.is_active)[0];
    const currentBillingPeriod = () => billingPeriods.find((el: any) => el.is_active);

    const months = useMemo(() => {
        return Array.from({ length: 12 }, (_, i) => i).map((i) => {
            return { value: `${i+1}`, label: moment.months()[i] };
        });
    }, []);

    const years = useMemo(() => {
        return [currentYear - 1, currentYear, currentYear + 1].map((i) => {
            return { value: `${i}`, label: `${i}` };
        });
    },[]);

    useEffect(() => {
        if (showError) {
            Toast(t(error?.reason ?? "SOMETHING_WENT_WRONG"), t(error?.cause_info));
            setShowError(false);
        }
    }, [showError, error, t]);

    const createBillingPeriod: SubmitHandler<BillingPeriod> = async (data: BillingPeriod) => {
        if (!data.month || !data.year || !moment(data.end_date).isValid())
        return Toast(t("PLEASE_PROVIDE_REQUIRED_VALUES"));

        dispatch(
            createAgencyBillingPeriod({
                agencyId: props.currentAgency.id,
                data: { ...data, month: Number(data.month) - 1, year: Number(data.year) },
            })
            ).then((e) => {
            if (e.type === "agencies/createBillingPeriod/rejected") {
                setShowError(true);
            } else {
                Toast(t("SAVED_SUCCESFULLY"));
            }
        });
    };

    const endBillingPeriod = async () => {
        await dispatch(endAgencyBillingPeriod(props.currentAgency.id)).then((e) => {
        if (e.type === "agencies/endBillingPeriod/rejected") {
            setShowError(true);
        } else {
            Toast(t("SAVED_SUCCESFULLY"));
        }
        });
    };
    
    return(
        <>
            {isLoading && <LoadingMask />}
            <ToastContainer progressStyle={{ background: "#D4AF37" }} />
            <div className="text-black max-h-full p-5 md:w-full">
                <div className="text-md font-bold text-black pl-3 mt-2">
                    {t("BILLING")}
                </div>
                <div className="py-5 w-full flex flex-wrap justify-items-center">
                    <div className="w-full sm:w-1/2 xl:w-1/3 text-center pt-3 px-5">
                        <label className="tracking-wide text-black font-bold text-lg mb-2">
                            {t("LAST_BILLING_PERIOD")}
                        </label>
                        <hr className="bg-blue mb-5 h-0.5"></hr>
                        {lastBillingPeriod() ? (
                            <label className="text-black text-base">
                                {moment({
                                    month: lastBillingPeriod().month,
                                    year: lastBillingPeriod().year,
                                }).format("MMMM, yyyy")}
                            </label>
                        ) : (
                            t("UNAVAILABLE")
                        )}
                    </div>
                    <div className="w-full sm:w-1/2 xl:w-1/3 text-center pt-3 px-5">
                        <label className="tracking-wide text-black font-bold text-lg mb-2">
                            {t("CURRENT_BILLING_PERIOD")}
                        </label>
                        <hr className="bg-blue mb-5 h-0.5"></hr>
                        {currentBillingPeriod() ? (
                            <div className="flex flex-col px-3 items-center">
                                <div className="mr-3">
                                    <label className="text-black text-base">
                                        {moment({
                                            month: currentBillingPeriod().month,
                                            year: currentBillingPeriod().year,
                                        }).format("MMMM, YYYY")}
                                    </label>
                                    <br></br>
                                    <label className="text-sm text-gray-600">
                                        {t("ENDS_ON", {
                                            end_date: moment(
                                            currentBillingPeriod().end_date
                                            ).format("MMMM DD, YYYY"),
                                        })}
                                    </label>
                                </div>
                                <Button
                                    onClick={() => endBillingPeriod()}
                                    classNames="mt-5"
                                    isTerciary={true}
                                >
                                    <span className="flex items-center text-red-600">
                                        <div className="w-8 p-1 aspect-square mr-2">
                                            <XMarkIcon className="aspect-square stroke-red-600" />
                                        </div>
                                        {t("END_NOW")}
                                    </span>
                                </Button>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <div className="w-full flex flex-row gap-2">
                                    <ReactiveFormSelect
                                        className="md:w-1/2"
                                        name="month"
                                        label={t("MONTH")}
                                        options={months}
                                        control={billingPeriodForm.control}
                                        isRequired
                                    />

                                    <ReactiveFormSelect
                                        className="md:w-1/2"
                                        name="year"
                                        label={t("YEAR")}
                                        value={`${currentYear}`}
                                        options={years}
                                        control={billingPeriodForm.control}
                                        isRequired
                                    />
                                </div>

                                <div className="w-full flex flex-col items-center">
                                    <ReactiveFormInput
                                        className="md:w-full"
                                        type="date"
                                        name="end_date"
                                        label={t("END_DATE")}
                                        control={billingPeriodForm.control}
                                        isRequired
                                    />

                                    <div className="pt-4">
                                        <Button
                                            onClick={billingPeriodForm.handleSubmit(createBillingPeriod)}
                                        >
                                            <span className="flex items-center font-semibold pr-3">
                                            <div className="w-8 p-1 aspect-square mr-2">
                                                <CheckIcon />
                                            </div>
                                            {t("START_BILLING_PERIOD")}
                                            </span>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}